// Shared chat-bubble renderer for Customer Assist sessions. Used on
// both the agent dialog and the anon /assist client page so the visual
// treatment stays identical from both sides — the only difference is
// which side `from === 'agent'` should be aligned to (agent's view
// puts agent right, client's view puts client right).
//
// Attachments render via the shared `<AttachmentTile>` (also used by
// internal notes) — keeps the visual language consistent.

import { cn } from '@/lib/utils';
import { AttachmentTile } from '@/components/AttachmentTile';
import type { AssistChatMessage, AssistRole } from '@/lib/use-assist-channel';

interface Props {
  message: AssistChatMessage;
  /** Which side is "me" — drives left/right alignment. */
  viewerRole: AssistRole;
  /** Optional display name for the OTHER side. On the client surface
   *  we pass the agent's actual name (snapshot from
   *  `customer_assist_sessions.agent_name`, migration 049) so bubbles
   *  read "Liz · 4:32 PM" rather than the generic "Agent". The agent
   *  surface leaves this undefined → falls back to the role label. */
  otherDisplayName?: string | null;
}

export function AssistChatBubble({ message, viewerRole, otherDisplayName }: Props) {
  const isMine = message.from === viewerRole;
  const isAudit = /^\[(requested|code shared)/.test(message.text);
  const otherLabel = otherDisplayName?.trim()
    ? otherDisplayName.trim()
    : message.from === 'agent'
      ? 'Agent'
      : 'Client';

  return (
    <li
      className={cn(
        'flex flex-col gap-0.5 rounded-md px-2.5 py-1.5 text-sm leading-snug',
        isMine ? 'self-end bg-primary/10 text-foreground' : 'self-start bg-muted text-foreground',
        // Audit lines (code request / code shared) get a subtle muted
        // treatment so they read as system events, not user chat. We
        // deliberately don't tint them amber — amber is reserved for
        // the live sensitive-content moment (the value banner), not
        // for the post-hoc audit trail.
        isAudit && 'self-center border border-dashed border-border/60 bg-muted/40 text-muted-foreground',
      )}
      style={{ maxWidth: '88%' }}
    >
      <span className="text-3xs uppercase tracking-wide text-muted-foreground">
        {isMine ? 'You' : otherLabel}
        {' · '}
        {new Date(message.ts).toLocaleTimeString(undefined, {
          hour: 'numeric',
          minute: '2-digit',
        })}
      </span>

      {message.text ? (
        <span
          className={cn(
            'whitespace-pre-wrap break-words',
            isAudit && 'italic',
          )}
        >
          {message.text}
        </span>
      ) : null}

      {message.attachments.length > 0 ? (
        <div className="mt-1 flex flex-col gap-1.5">
          {message.attachments.map((att, i) => (
            <AttachmentTile key={`${att.url}-${i}`} att={att} />
          ))}
        </div>
      ) : null}
    </li>
  );
}

